import { Theme, css, useTheme } from '@emotion/react';
import { FC, useState } from 'react';

import { AddressSuggestion, Recipient } from 'api/api';

import { Spinner } from '~src/commons/components';
import { useTranslations } from '~src/commons/utils/translations/translate';
import { useCountryRegionsLocalizationQuery } from '~src/country-regions/country-regions';
import { FullscreenComponent, FullscreenMode } from '~src/fullscreen-internals/fullscreen-mode';
import { useGlobalState } from '~src/global-state';
import { AnimatedButton } from '~src/ui/button';
import { Dialog } from '~src/ui/dialog';
import { DialogLayout } from '~src/ui/dialog-layout';
import { DisclaimerMessage } from '~src/ui/disclaimer-message';
import { Heading } from '~src/ui/heading';
import { Footer } from '~src/widget/footer';

import { useFSMContext } from '../context/use-fsm-context';
import { formatAddressDiff } from '../utils/format-ui';

interface VerifyAddressModalProps {
  enteredAddress?: Recipient;
  suggestion?: AddressSuggestion;
  isSubmissionBlocking?: boolean;
  handleClose: () => void;
  handlePrimaryButton: (address: Recipient) => void;
  handleSecondaryButton: (address: Recipient) => void;
}

export const VerifyAddressModal: FullscreenComponent<VerifyAddressModalProps> = ({
  enteredAddress,
  suggestion,
  isSubmissionBlocking,
  handleClose,
  handlePrimaryButton,
  handleSecondaryButton,
}) => {
  const globalState = useGlobalState();
  const theme = useTheme();
  const { tICU } = useTranslations();
  const regions = useCountryRegionsLocalizationQuery();
  const { recipientState, billingState } = useFSMContext();
  // helper to distinguish which button we should animate
  const [clicked, setClicked] = useState<'primary' | 'secondary'>();

  if (!enteredAddress || !suggestion) {
    return null;
  }

  const [entered, suggested] = formatAddressDiff(
    theme,
    enteredAddress,
    suggestion,
    globalState.purchase_country,
    regions.data?.translations
  );

  return (
    <Dialog height={770} width={520} onClose={handleClose}>
      <DialogLayout title={tICU('widget.validation.VERIFY_YOUR_ADDRESS')} onClose={handleClose}>
        <DisclaimerMessage
          message={
            isSubmissionBlocking
              ? 'validation.INVALID_ADDRESS_DISCLAIMER'
              : 'validation.SIMILAR_ADDRESS_DISCLAIMER'
          }
          color="superlightGrey"
        />
        <div css={styles.addressDiff}>
          <div>
            <Heading>{tICU('widget.validation.ENTERED')}</Heading>
            <div css={styles.addressContainer}>{entered}</div>
          </div>
          <div>
            <Heading>{tICU('widget.validation.SUGGESTED')}</Heading>
            <div css={styles.addressContainer}>{suggested}</div>
          </div>
        </div>
        <div css={styles.buttonWrapper}>
          <AnimatedButton
            animKey={`${clicked === 'primary'}`}
            onClick={() => {
              handlePrimaryButton({ ...enteredAddress, ...suggestion.address });
              setClicked('primary');
            }}
          >
            {(recipientState.meta.fetching || billingState?.meta.fetching) &&
            clicked === 'primary' ? (
              <Spinner size="small" />
            ) : (
              tICU('widget.validation.USE_SUGGESTED')
            )}
          </AnimatedButton>
          <AnimatedButton
            animKey={`${clicked === 'secondary'}`}
            onClick={e => {
              e.preventDefault();
              handleSecondaryButton(enteredAddress);
              setClicked('secondary');
            }}
            buttonType="secondary"
          >
            {isSubmissionBlocking ? (
              tICU('widget.validation.EDIT_ENTERED')
            ) : (recipientState.meta.fetching || billingState?.meta.fetching) &&
              clicked === 'secondary' ? (
              <Spinner size="small" />
            ) : (
              tICU('widget.delivery_address.CONTINUE_ANYWAY_BTN')
            )}
          </AnimatedButton>
        </div>
        <Footer />
      </DialogLayout>
    </Dialog>
  );
};

VerifyAddressModal.fullscreenName = 'VerifyAddressModal';

export const VerifyAddressModalProxy: FC<VerifyAddressModalProps> = props => {
  return (
    <FullscreenMode>
      <VerifyAddressModal {...props} />
    </FullscreenMode>
  );
};

const styles = {
  addressDiff: css({
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  }),
  addressContainer: (theme: Theme) => ({
    padding: '12px 16px',
    border: `1px solid ${theme.neutral91}`,
    borderRadius: '6px',
  }),
  buttonWrapper: css({
    margin: '32px 0',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  }),
};
